import type { PresetV2 } from "./mockData";
import { RUBRO_HOGAR_DECO } from "../../lib/rubro";
import { MODO_MODELO, palabraDeModeloActual } from "../../lib/nombreDeModelo";

// Feature flag del modo "Video con influencer". Mientras esté en `false` el modo queda
// OCULTO para TODOS —el design-row de la home V2, la pantalla de Modos, el sheet de
// Generar, la sidebar y el Agente— y además su URL deja de abrir, porque `InterfazV2` no
// arma su card (`CARD_POR_KIND['video-influencer']` queda en `null`). Con `true` es
// visible para todos los planes (ADR-561): la restricción Pro/Ultra aplica al GENERAR
// (VideoPlanRestrictionModal vía checkGenerationGuards).
//
// APAGADO el 2026-09-17 — ADR-2026-09-17-video-con-influencer-se-apaga-entero. Apagarlo
// NO es borrarlo: la entrada sigue en `MODES`, las pantallas siguen, `camposDeModo` y las
// skills siguen, y las generaciones con `modo_utilizado = 'video_influencer'` no se tocan.
// Volver a prenderlo es cambiar esta línea.
//
// El tipo va explícito y no el literal inferido: con `= false` a secas TypeScript narrowea
// a `false` cada condición que lo usa y dejan de leerse como condiciones.
export const VIDEO_INFLUENCER_ENABLED: boolean = false;

// Los modos "Catálogo de muebles" (ADR-582) e "Infografías" (ADR-814) YA NO
// tienen feature flag: se habilitaron y ahora se gatean por el rubro de la
// MARCA activa — ver `soloHogarDeco` acá abajo y
// ADR-2026-07-31-modos-muebles-por-rubro-de-marca.
//
// La rama del rediseño había sacado `INFOGRAFIA_ENABLED` por su cuenta, con la MISMA
// regla de rubro pero sólo para Infografía y dejando `CATALOGO_MUEBLES_ENABLED` en
// `false`. Al mergear gana este sistema, que es más completo: cubre los dos modos y
// además esconde los que NO aplican a muebles (`ocultoEnHogarDeco`) —
// ADR-2026-08-05-un-solo-gate-de-rubro-para-los-modos.

// Catálogo de "modos" de generación de Interfaz V2 (los mismos que aparecen en
// la home V2 / el design-row). Centralizado acá para reusarlo en la pantalla
// de Modos.
//
// Cada modo incluye el objeto `preset` EXACTO que usa InterfazV2 para abrir el
// sheet del modo (mismo id/kind/imágenes que las cards sintéticas de la home).
// Se pasa como `state.presetData` al navegar para que InterfazV2 monte el sheet
// en el primer paint (mecanismo `initialNavPreset`) y no se vea un flash de la
// home V2.

/**
 * Catálogo IA (`catalogo-ia`, ruta `/interfaz-v2/modo/catalogo`).
 *
 * Nació para cubrir también al banco de pruebas de diseño `catalogo-ia-v2`, que se
 * eliminó (ADR-2026-08-21-se-elimina-catalogo-v2). Se mantiene como helper —en vez de
 * volver a los `kind === 'catalogo-ia'` literales— porque es el único lugar donde queda
 * anotado qué comparaciones deciden FLUJO: si mañana vuelve a haber una variante del
 * modo, son exactamente estas.
 *
 * Vive acá y no en `InterfazV2.tsx` porque lo consume también `PresetDetailSheet`, y ese
 * import al revés cerraría un ciclo (InterfazV2 → PresetDetailSheet → InterfazV2).
 */
export const esCatalogoIA = (kind?: string | null): boolean =>
  kind === 'catalogo-ia';

/**
 * El modo Modelo (`design-modelo`, ruta `/interfaz-v2/modo/modelo`).
 *
 * Es el reemplazo de los `kind === 'design-modelo'` sueltos que decidían FLUJO: qué steps
 * tiene el modo, a dónde vuelve el "atrás", qué payload se arma al generar. Nació para
 * cubrir también a las tres variantes del banco de pruebas del rediseño
 * (`design-modelo-v1/-v2/-v3`), que se borraron cuando la v2 se promovió al modo real
 * (ADR-2026-09-02-modelo-v2-es-el-modo-modelo). Se mantiene —en vez de volver a las
 * comparaciones literales— porque es el único lugar donde queda anotado qué comparaciones
 * deciden flujo: si mañana vuelve a haber una variante del modo, son exactamente éstas.
 */
export const esDesignModelo = (kind?: string | null): boolean =>
  kind === 'design-modelo';

/**
 * El modo "Crear desde cero" y su variante `asistente-v2` —
 * ADR-2026-09-08-asistente-v2-es-una-variante-del-mismo-modo.
 *
 * Es el mismo `AsistenteMarketingSheet` con `variante='v2'`: mismo chat, mismos
 * adjuntos, mismo historial. Lo único que cambia es que v2 NO muestra el switch
 * Imagen/Video ni el desplegable de Ayuda de IA —la ayuda va prendida por detrás y el
 * tipo de salida se pide por texto.
 *
 * Este helper es el gemelo de `esCatalogoIA` / `esDesignModelo` y existe por el mismo
 * motivo: es el único lugar donde queda anotado qué comparaciones deciden FLUJO
 * (qué paso monta `InterfazV2`, qué sheet abre, cuándo se cierra por URL). Los
 * `kind === 'asistente'` sueltos se reemplazaron por esto: dejar uno afuera hacía que
 * v2 cayera en el flujo de "elegir producto" y abriera el sheet equivocado.
 */
export const esAsistente = (kind?: string | null): boolean =>
  kind === 'asistente' || kind === 'asistente-v2';

/**
 * Modos que eligen el fondo con la BIBLIOTECA completa: la vista por categorías
 * (Automático · Añadir fondo · Fondo blanco · Mis fondos · Ciudad…), con "Agregar fondo"
 * como segunda acción y los ajustes de tono/luz del fondo blanco adentro del picker.
 *
 * El resto de los modos abre la grilla plana con todos los fondos sueltos y la segunda
 * acción "Describir".
 *
 * Empezó siendo sólo el Catálogo y se llevó a `design-modelo`: eran dos bibliotecas
 * distintas para la misma decisión, y la del Catálogo es la que quedó. En Modelo, además,
 * es ahora el ÚNICO lugar donde se define el fondo —el rediseño de la pantalla sacó esa
 * sección del panel de configuración—, así que los ajustes del fondo blanco tienen que
 * estar acá o no están en ningún lado.
 */
export const usaBibliotecaDeFondos = (kind?: string | null): boolean =>
  esCatalogoIA(kind) || esDesignModelo(kind);

export interface ModeCard {
  /** Segmento de ruta del modo en V2: `/interfaz-v2/modo/<path>`. */
  path: string;
  /** Carátula en video (mp4 corto, autoplay loop muteado). Opcional: los modos
   *  sin video propio (modelo, catálogo-muebles, infografía) muestran
   *  `preset.imageUrl` estático en su lugar (los consumidores caen a `<img>` si
   *  no hay video). */
  video?: string;
  /** Portada estática del modo. Con `video`, es el poster mientras carga; SIN
   *  `video` sigue siendo la portada que consume la tira del inicio
   *  (`useModosGenerar`: `arte?.poster || m.poster || m.preset.imageUrl`). */
  poster?: string;
  /** Preset que abre el modo en V2 (espeja las cards del design-row). */
  preset: PresetV2;
  /** Si true, el modo solo se muestra a dev testers (los consumidores de
   *  `MODES` deben filtrar con `isDevTester`). */
  devOnly?: boolean;
  /**
   * Si true, el modo NO aparece en ninguna lista de descubrimiento (`/modos`, la tira
   * de la home, el sheet de Generar de mobile, el drawer de Mi marca, "Todas las
   * herramientas" de la sidebar) pero SIGUE VIVO en el catálogo —
   * ADR-2026-08-20-anuncio-sale-de-las-listas-sin-salir-del-catalogo.
   *
   * Es un flag y no un borrado de `MODES` a propósito: la entrada la consumen cosas
   * que NO son listas y que tienen que seguir funcionando —`MODE_PATH_TO_KIND` /
   * `MODE_KIND_TO_PATH`, `PORTADA_CUADRADA`, `anuncioTercero()`, y los
   * `MODES.find(m => m.path === 'anuncio')` de `GeneratedImagesInline` y
   * `GeneratedImagesHistory` que arman el presetData del atajo desde una imagen
   * abierta—. Borrar la entrada rompía justo los accesos que se querían conservar.
   *
   * Lo respeta `modosVisibles()`; el deep-link `/interfaz-v2/modo/<path>` no lo mira.
   */
  ocultoEnListas?: boolean;
  /** Si true, el modo solo se muestra cuando la MARCA activa es del rubro
   *  "Hogar y Decoración" (los consumidores de `MODES` deben filtrar con
   *  `esRubroHogarDeco(rubro de useRubroMarcaActiva)`). Reemplaza a los viejos
   *  flags CATALOGO_MUEBLES_ENABLED / INFOGRAFIA_ENABLED —
   *  ADR-2026-07-31-modos-muebles-por-rubro-de-marca. */
  soloHogarDeco?: boolean;
  /** Inverso de `soloHogarDeco`: el modo se OCULTA cuando la marca activa es de
   *  "Hogar y Decoración". Para los modos que ponen el producto sobre un modelo
   *  IA, que no aplican a un mueble —
   *  ADR-2026-07-31-hogar-deco-sin-plantillas-ni-modelo. */
  ocultoEnHogarDeco?: boolean;
  /** Gemelo de `soloHogarDeco` para "Belleza y Cuidado Personal": el modo sólo se
   *  muestra cuando la MARCA activa es de ese rubro (los consumidores de `MODES`
   *  filtran con `esRubroBelleza(rubro de useRubroMarcaActiva)`) —
   *  ADR-2026-09-10-modo-mostrar-como-funciona-belleza.
   *
   *  Es un eje NUEVO y no un reuso del de hogar: los dos rubros son excluyentes entre
   *  sí, pero un modo `soloBelleza` tiene que esconderse también de Moda, de
   *  Gastronomía y de cualquier otro, cosa que `!soloHogarDeco` no dice. */
  soloBelleza?: boolean;
  /** Gemelo de `soloBelleza` para "Moda e Indumentaria": el modo sólo se muestra cuando
   *  la MARCA activa es de ese rubro (`esRubroModa`) —
   *  ADR-2026-09-17-el-modo-diseno-es-solo-para-moda. Eje propio: `!soloHogarDeco &&
   *  !soloBelleza` no dice "sólo Moda". */
  soloModa?: boolean;
  /** Ruta dedicada FUERA de InterfazV2. Si está presente, los consumidores del catálogo (Modos,
   *  Estilos/contenidoAtlas) navegan ACÁ en vez de a `/interfaz-v2/modo/<path>` — InterfazV2 no monta
   *  este modo porque tiene pantalla propia. Ej: Shorts Studio (`/shorts-studio`). Centraliza el
   *  "route requirement" en el catálogo en vez de un hardcode por cada consumidor. */
  route?: string;
}

// Orden igual al design-row de la home V2: [auto, modelo, catálogo,
// video-influencer, asistente, anuncio].
export const MODES: ModeCard[] = [
  {
    // Modo "Shorts Studio" — pantalla propia (ruta /shorts-studio); Modos.tsx intercepta
    // este path y navega ahí en vez de a InterfazV2.
    path: "shorts-studio",
    route: "/shorts-studio",
    // Carátula PROPIA en video — ADR-2026-09-24-shorts-studio-estrena-caratula. Hasta acá
    // era un PLACEHOLDER: un gradiente fucsia con el nombre escrito, embebido como data-URI.
    // El clip cuenta lo que hace el modo (tres estilos a la izquierda, el vertical
    // transformado a la derecha), 1152×768 / 8 s, misma receta que Creador de video.
    video: "/images/modo_shorts_studio/caratula.mp4",
    // Cuadro 0 del clip YA comprimido, misma regla que Creador de video, Flujos y Animar
    // imagen: lo quieto y lo que arranca son idénticos, así que no hay salto. Y no es
    // cosmético: `preload` de la tira de la home se apaga cuando hay poster
    // (ADR-2026-08-20-el-video-de-animar-era-el-unico-que-preloadeaba).
    poster: "/images/modo_shorts_studio/caratula_poster.jpg",
    // Sólo dev testers — ADR-2026-08-20-shorts-studio-vuelve-a-interno. El modo estaba
    // listado para todos con carátula PLACEHOLDER. Con el
    // flag sale de `/modos`, del sheet de Generar, del drawer de Mi marca y de la
    // sidebar; la ruta `/shorts-studio` sigue accesible por URL para poder probarlo.
    //
    // Efecto colateral buscado: en el sheet y en el drawer este modo era un CLICK
    // MUERTO —los dos arman `/interfaz-v2/modo/shorts-studio`, que `InterfazV2` no
    // resuelve porque el modo tiene pantalla propia—, así que sacarlo de esas dos
    // listas apaga un bug que estaba vivo en producción.
    devOnly: true,
    preset: {
      id: "shorts-studio-card",
      title: "Shorts Studio",
      description:
        "Subí tu video hablando a cámara y transformalo por completo en un estilo: claymation, cómic, acuarela y más.",
      // El modo devuelve un VIDEO. Faltaba, y `outputType` es lo que decide la
      // familia del modo en todos lados (el sheet de Generar vía `useModosGenerar`,
      // y desde ADR-2026-08-31-la-sidebar-agrupa-por-video-e-imagen también los
      // grupos de la sidebar y los separadores de `/modos`). Sin esto, Shorts Studio
      // —un modo que transforma un video— quedaba clasificado como imagen.
      outputType: "video",
      // El cuadro 0 de su propio clip, no el gradiente placeholder.
      imageUrl: "/images/modo_shorts_studio/caratula_poster.jpg",
      imageUrlFull: "/images/modo_shorts_studio/caratula_poster.jpg",
      kind: "shorts-studio",
    },
  },
  {
    // Modo "Animar imagen" — pantalla propia (ruta /animar-imagen); Modos.tsx
    // navega ahí por `route`, igual que Shorts Studio. Convierte una foto (subida
    // o generada) en video: hasta ahora era un botón escondido adentro del
    // historial y del visor inline. Ver ADR-2026-08-14-modo-animar-imagen.
    //
    // Carátula PROPIA y APAISADA — ADR-2026-09-23-animar-imagen-deja-de-usar-el-clip-de-todos.
    // Era un PLACEHOLDER: el clip explicativo VERTICAL (4:5) del panel de Animar, que en
    // el slot 3:2 de /modos se recortaba, y encima a 480 px de ancho contra los 992 que
    // la card necesita en retina.
    //
    // El clip viejo NO se movió ni se renombró: `video_animar_imagen.mp4` sigue suelto en
    // la raíz de `/images/` porque lo usan otros TRECE lugares (el menú de video, el modal
    // de restricción de plan, tres pantallas de configurar, el historial, el inline de
    // creaciones, los ejemplos del modo, el hero de la propia pantalla…) como clip
    // ilustrativo genérico de "esto se va a mover". En esas cajas es 4:5 y está bien: el
    // problema nunca fue el clip sino meterlo en un slot apaisado.
    path: "animar-imagen",
    route: "/animar-imagen",
    video: "/images/modo_animar_imagen/caratula.mp4",
    // El poster NO es cosmético: es lo que corta el preload —
    // ADR-2026-08-20-el-video-de-animar-era-el-unico-que-preloadeaba.
    //
    // La tira de la home dibuja `preload={modo.poster ? 'none' : 'metadata'}`
    // (`EstiloMinimal.tsx`), o sea que la regla se invertía justo contra el archivo más
    // pesado: éste era el ÚNICO modo con `video` y sin `poster`, así que era el único
    // que arrancaba pidiendo bytes al montar, mientras los dos videos que sí tienen
    // poster iban en `preload="none"`.
    //
    // De paso resuelve el otro problema que tenía: sin poster, si el video no decodifica
    // la card quedaba gris y sin nada que mostrar.
    //
    // Es el CUADRO 0 del clip ya comprimido, misma regla que Creador de video y Flujos:
    // lo quieto y lo que arranca son idénticos, así que no hay salto.
    poster: "/images/modo_animar_imagen/caratula_poster.jpg",
    preset: {
      id: "animar-imagen-card",
      title: "Animar imagen",
      description:
        "Convertí cualquier foto en un video con movimiento realista.",
      // El cuadro 0 de su propio clip, no la miniatura PRESTADA de Video con
      // influencer que traía — ADR-2026-09-23-animar-imagen-deja-de-usar-el-clip-de-todos.
      imageUrl: "/images/modo_animar_imagen/caratula_poster.jpg",
      imageUrlFull: "/images/modo_animar_imagen/caratula_poster.jpg",
      kind: "animar-imagen",
      outputType: "video",
      creditosPorUnidad: 1,
      unidadConsumo: "segundo",
    },
  },
  {
    // Modo "Carrusel" — pantalla propia (ruta /carrusel); Modos.tsx navega ahí por
    // `route`, igual que Animar imagen. Genera 2-6 tomas nuevas del mismo producto
    // a partir de una foto (subida o generada): hasta ahora era un botón escondido
    // adentro del historial y del visor inline. Ver ADR-2026-08-14-modo-carrusel.
    //
    // Sin `video` propio → Modos.tsx y HomeCards caen a `<img>`.
    //
    // Carátula NUEVA — ADR-2026-09-23-las-cuatro-que-faltaban. La anterior era un lienzo
    // 1:1 con el abanico chiquito en el medio y márgenes enormes: en el slot 3:2 el
    // `object-cover` recortaba arriba y abajo y quedaba diminuta. Encima el abanico pasó
    // a ser la silueta de Plantillas, y sus tres tomas eran casi el mismo recorte, así
    // que "otros ángulos" no se leía. La nueva es una fila de cuatro tomas del MISMO
    // momento, cortada por los bordes: el gesto de deslizar un carrusel.
    //
    // Estrena `poster`, igual que Fotos de producto: antes caía al respaldo.
    path: "carrusel",
    route: "/carrusel",
    poster: "/images/modo_carrusel/caratula.jpg",
    preset: {
      id: "carrusel-card",
      title: "Carrusel de imágenes",
      description:
        "De una foto, varias: nuevas tomas del mismo producto con otros ángulos y encuadres.",
      imageUrl: "/images/modo_carrusel/caratula.jpg",
      imageUrlFull: "/images/modo_carrusel/caratula.jpg",
      kind: "carrusel",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    // Automática va con carátula FIJA — ADR-2026-08-05-automatica-va-con-foto-y-sin-video.
    // Sin `video`: los consumidores caen a `<img>` con el `poster`, igual que Anuncio,
    // catálogo-muebles e infografía. Era el ÚLTIMO modo con carátula en video (el
    // holograma), y encima tenía dos —éste y el "nuevo" que le pisaba el carrusel del
    // shell—, así que se veía distinto según la pantalla.
    //
    // La de acá es la carátula GENÉRICA, la que ve cualquier rubro. Hogar y Decoración
    // tiene la suya en `PORTADA_POR_RUBRO`.
    //
    // Carátula NUEVA — ADR-2026-09-23-las-cuatro-que-faltaban. La anterior era un collage
    // de scrapbook con SEIS productos distintos (cartera, camisa, jean, vestido,
    // botinetas, anteojos): mostraba variedad de cosas cuando el modo hace lo contrario
    // —UN producto, muchas escenas inventadas para él—. La nueva es la misma botella en
    // cuatro mundos deliberadamente opuestos, con su packshot de origen en chico.
    path: "auto",
    poster: "/images/modo_automatico/caratula.jpg",
    preset: {
      id: "imagen-auto-card",
      title: "Generar imagen automáticamente",
      description:
        "Sumá tu producto y la IA arma todo: fondo, escena y luz por vos.",
      // La misma foto que el `poster`, por lo mismo que en Anuncio: si la card usa una y
      // la tira otra, el modo cambia de cara según quién lo pinte.
      imageUrl: "/images/modo_automatico/caratula.jpg",
      imageUrlFull: "/images/modo_automatico/modo_auto.png",
      kind: "design-auto",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    // Portada ÚNICA y estática (ADR-2026-08-04-modelo-portada-unica): el collage
    // "producto + fondo + cara → resultado" reemplaza a la carátula en video y a
    // los dos assets estáticos que había (`fot_modelo.png` como thumbnail y
    // `modelo_gia.png` como hero del detail sheet).
    //
    // Sin `video` a propósito — no es un olvido: todos los consumidores
    // (`/modos`, HomeCards, SheetModosProducto, EstiloMinimal) ya caen a `<img>`
    // cuando el modo no trae video, igual que catálogo-muebles e infografía.
    //
    // `poster` queda apuntando a la misma imagen porque `useModosGenerar` arma la
    // tira del inicio con `arte?.poster || m.poster || m.preset.imageUrl` — es el
    // canal por el que la portada llega ahí.
    //
    // Carátula NUEVA — ADR-2026-09-22-la-caratula-de-flujos-pasa-a-video. La anterior contaba lo
    // mismo con un diagrama: tres tarjetas tildadas y una flecha azul confluyendo en el
    // resultado. Ese idioma pasó a ser el de Flujos (cables y puertos entre tarjetas), y
    // dos cards de la misma grilla no pueden dibujar la misma figura. Acá la relación se
    // lee sin flecha ni tildes: se repiten la prenda, la cara y el ambiente entre las
    // piezas chicas y el resultado grande.
    path: "modelo",
    poster: "/images/modo_con_modelo/caratula.jpg",
    preset: {
      id: "imagen-con-modelo",
      title: "Generar imagen personalizada",
      // La palabra sigue al rubro de la marca — "Modelo" en Moda, "Avatar" en el
      // resto (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo). Es un
      // `get` y no un string porque `MODES` se arma UNA vez al cargar el módulo y la
      // marca activa se cambia sin recargar la app: leído al momento de pintarlo, el
      // texto ya viene con la palabra de la marca con la que se está trabajando.
      get description() {
        return `Tu producto sobre un ${palabraDeModeloActual().singular} IA en la pose y el fondo que vos elijas.`;
      },
      imageUrl: "/images/modo_con_modelo/caratula.jpg",
      // El hero del detail sheet ("Inicio / Modelo") pasa a un collage VERTICAL propio
      // (`..._portada2.png`): prenda + fondo + cara → resultado, o sea las tres cosas
      // que se eligen en el modo y en qué terminan. Reemplaza a `modelo_gia.png`
      // — ADR-2026-08-20-modelo-hero-collage-vertical.
      //
      // No es el mismo archivo que la carátula de arriba, aunque cuenten lo mismo: esa
      // es apaisada, para los slots 3:2 de `/modos` y de la tira del inicio, y ésta es
      // vertical, para la columna angosta de la pantalla del modo. La de acá NO se
      // reusa como carátula ni al revés: en su slot, cada una se recorta.
      imageUrlFull: "/images/modo_con_modelo/modo_modelo_portada2.png",
      kind: "design-modelo",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    // El producto va sobre un modelo IA: no aplica a muebles.
    ocultoEnHogarDeco: true,
  },
  {
    // Modo "Fotos de producto" (ADR-2026-08-13-modo-fotos-producto). El producto
    // SOLO, sin modelo: se elige una o varias vistas (lateral, plano frente,
    // maniquí invisible…) y el fondo (blanco o escena descrita). Va pegado a
    // "Imagen personalizada" porque es su hermano sin modelo.
    //
    // Carátula PROPIA — ADR-2026-09-23-las-cuatro-que-faltaban. Hasta acá era un
    // PLACEHOLDER: una miniatura suelta de una zapatilla prestada del catálogo interno
    // de vistas (`/images/multi-vistas/zapatillas/lateral.webp`), o sea UNA foto para un
    // modo cuya bajada dice "en varias vistas". La nueva muestra las cuatro vistas del
    // mismo producto —frente y espalda en maniquí invisible, doblada y detalle—, que es
    // lo que el modo entrega.
    //
    // Estrena `poster`: antes caía al respaldo (`preset.imageUrl`) porque la entrada no
    // declaraba ninguno. Con el campo puesto, `caratulaModo` la resuelve por el mismo
    // camino que el resto de los modos.
    // Duplicada con la card sintética de InterfazV2 (patrón de todos los modos):
    // si tocás una, tocá la otra.
    path: "fotos-producto",
    poster: "/images/modo_fotos_producto/caratula.jpg",
    preset: {
      id: "fotos-producto-card",
      title: "Fotos de producto",
      description:
        "Tu producto solo, en las vistas que necesites: fondo blanco o la escena que describas.",
      // `imageUrl` = la misma carátula que el `poster`, para que la card y la tira no
      // muestren cosas distintas.
      // `imageUrlFull` = hero de "Elegí el producto": el collage antes → después
      // (prenda en percha → foto limpia) que muestra qué hace el modo.
      imageUrl: "/images/modo_fotos_producto/caratula.jpg",
      imageUrlFull: "/images/modo_fotos_producto/hero_producto.png",
      kind: "fotos-producto",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    // Oculto para Hogar y Decoración — ADR-2026-08-28-fotos-de-producto-no-va-en-muebles
    // (supersede la decisión contraria que estaba anotada en InterfazV2: "un mueble
    // también se fotografía solo"). El modo se define por su picker de VISTAS, y ese
    // picker es de indumentaria: maniquí frontal/trasero/ángulo, maniquí mujer y
    // prenda doblada. A una marca de muebles le ofrecía un modo cuyo grueso de
    // opciones no le aplica.
    ocultoEnHogarDeco: true,
  },
  {
    path: "catalogo",
    video: "/images/modo_catalogo_ia/modo_catalogo_caratula_desktop.mp4",
    poster: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
    preset: {
      id: "catalogo-ia-card",
      title: "Generar catálogo con IA",
      description:
        "Photoshoots por producto con varias vistas y poses en un solo proceso.",
      imageUrl: "/images/modo_con_modelo/fot_modelo.png",
      // Hero propio y NO el `modo_catalogo_hero.png` de antes: ese asset lo comparten
      // catálogo de muebles, infografía y el asistente, así que cambiarlo ahí les
      // cambiaba la portada a los cuatro. Ver ADR-2026-08-26-catalogo-hero-propio.
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero2.png",
      kind: "catalogo-ia",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    // Su reemplazo para hogar/deco es el catálogo de muebles, que se muestra con
    // este mismo título — ADR-2026-08-01-hogar-deco-solo-catalogo-muebles-renombrado.
    ocultoEnHogarDeco: true,
  },
  {
    // Modo "Catálogo" para Hogar y Decoración (ADR-2026-10-13-catalogo-con-el-agente):
    // una pantalla propia (`/catalogo`, `/catalogo/:sesionId`) donde el agente arma el plan
    // de tomas y un escritor del backend redacta los prompts. Reemplazó a `catalogo-muebles`
    // (ADR-2026-10-14-catalogo-reemplaza-a-catalogo-muebles); el kind viejo sigue en el sheet
    // sólo porque Infografía comparte su flujo.
    path: "catalogo-agente",
    route: "/catalogo",
    preset: {
      id: "catalogo-agente-card",
      title: "Catálogo",
      description: "Contale qué catálogo querés y el agente arma las tomas de cada producto.",
      // Carátula propia: varios muebles en abanico sobre el crema de la card Catálogos del inicio
      // (ADR-2026-10-15-la-caratula-del-catalogo-con-el-agente).
      imageUrl: "/images/modo_catalogo_agente/caratula-crema.jpg",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "catalogo-agente",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    soloHogarDeco: true,
  },
  {
    // Modo "Infografía" (ADR-813): fichas técnicas dimensionales de los productos.
    // Reusa el flujo y el motor del catálogo de muebles pero salteando el picker de
    // vistas (su única vista es la infografía). Mismo gate de rubro de marca
    // que el catálogo de muebles (`soloHogarDeco`).
    // Card SIN video propio: muestra `imageUrl` estático (una infografía con cotas,
    // ADR-817); el hero de "Elegí el producto" lo pone ProductHeroCollage.
    path: "infografia",
    preset: {
      id: "infografia-card",
      title: "Infografías de producto con IA",
      description:
        "Fichas técnicas con las medidas y características de cada uno de tus productos.",
      imageUrl: "/images/modo_infografia/hero_foto.jpg",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "infografia",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    soloHogarDeco: true,
  },
  {
    // Modo "Variantes de acabado" — pantalla propia (ruta /variantes-de-acabado);
    // Modos.tsx navega ahí por `route`, igual que Animar imagen. El MISMO mueble, una
    // imagen por material o color: el aparador en roble, en nogal, en terracota. Sólo
    // Hogar y Decoración (`soloHogarDeco`), como el catálogo de muebles.
    // Ver ADR-2026-09-03-modo-variantes-de-acabado.
    //
    // NO es una vista del Catálogo: allá la cantidad de imágenes es "producto × vista",
    // y acá un solo producto sale N veces con la N que marcan los acabados.
    //
    // Carátula PLACEHOLDER: el packshot del Catálogo del rubro. Falta un cuadro propio
    // con el mismo mueble en tres acabados.
    path: "variantes-de-acabado",
    route: "/variantes-de-acabado",
    poster: "/images/modo_catalogo_muebles/hero_packshot.png",
    preset: {
      id: "variantes-acabado-card",
      title: "Variantes de acabado",
      description:
        "El mismo mueble en cada material y color que ofrecés, partiendo de tu foto.",
      imageUrl: "/images/modo_catalogo_muebles/hero_packshot.png",
      imageUrlFull: "/images/modo_catalogo_muebles/hero_packshot.png",
      kind: "variantes-acabado",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    soloHogarDeco: true,
    // Abierto a todas las marcas de Hogar y Decoración: genera contra el backend
    // (ADR-2026-09-23-variantes-de-acabado-se-conecta-al-backend) y deja de ser interno —
    // ADR-2026-09-30-variantes-y-ambiente-salen-del-candado-dev (supersede, para este
    // modo, ADR-2026-09-11-los-tres-modos-de-hogar-y-deco-son-internos-hasta-que-generen).
  },
  {
    // Modo "Mostrar cómo funciona" — pantalla propia (ruta /mostrar-como-funciona);
    // Modos.tsx navega ahí por `route`, igual que Animar imagen. Video corto (5-8 s) del
    // mueble en uso: alguien lo usa, la cámara recorre el ambiente, o el mueble se
    // transforma. Sólo Hogar y Decoración (`soloHogarDeco`), como el catálogo de muebles.
    // Ver ADR-2026-09-03-modo-mostrar-como-funciona.
    //
    // Carátula PLACEHOLDER: el ropero en contexto del Catálogo del rubro. Falta un cuadro
    // propio de un mueble en uso.
    path: "mostrar-como-funciona",
    route: "/mostrar-como-funciona",
    poster: "/images/modo_catalogo_muebles/hero_contexto.jpg",
    preset: {
      id: "mostrar-como-funciona-card",
      title: "Mostrar cómo funciona",
      description:
        "Un video corto del mueble en uso: alguien se sienta, la cámara recorre o se transforma.",
      imageUrl: "/images/modo_catalogo_muebles/hero_contexto.jpg",
      imageUrlFull: "/images/modo_catalogo_muebles/hero_contexto.jpg",
      kind: "mostrar-como-funciona",
      outputType: "video",
      creditosPorUnidad: 1,
      unidadConsumo: "segundo",
    },
    soloHogarDeco: true,
    // Interno hasta que genere de verdad (su Generar todavía es un mock; sus dos hermanos
    // ya salieron — ADR-2026-09-30-variantes-y-ambiente-salen-del-candado-dev) —
    // ADR-2026-09-11-los-tres-modos-de-hogar-y-deco-son-internos-hasta-que-generen.
    devOnly: true,
  },
  {
    // Modo "Ambiente completo" — pantalla propia (ruta /ambiente-completo); Modos.tsx
    // navega ahí por `route`, igual que "Mostrar cómo funciona". De 2 a 4 productos de la
    // marca en UNA sola foto de ambiente, con un rol por producto. Sólo Hogar y
    // Decoración (`soloHogarDeco`), como el resto de los modos del rubro.
    // Ver ADR-2026-09-03-modo-ambiente-completo.
    //
    // Va PEGADO al Catálogo de muebles y a "Mostrar cómo funciona" en el orden de la
    // lista: son los tres del rubro, y quedar separados los hacía leer como modos de
    // familias distintas.
    //
    // Carátula PROPIA del modo: un living armado —sillón, mesa baja con libros, alfombra,
    // aplique— o sea varios productos de la marca conviviendo en UNA escena, que es
    // justo lo que promete el modo. Reemplaza al placeholder que era el ropero en
    // contexto del Catálogo del rubro (un solo producto, y prestado de otro modo).
    path: "ambiente-completo",
    route: "/ambiente-completo",
    poster: "/images/modo_ambiente_completo/portada.png",
    preset: {
      id: "ambiente-completo-card",
      title: "Ambiente completo",
      description:
        "Varios productos tuyos en una sola foto de ambiente: living, comedor o dormitorio armado.",
      imageUrl: "/images/modo_ambiente_completo/portada.png",
      imageUrlFull: "/images/modo_ambiente_completo/portada.png",
      kind: "ambiente-completo",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    soloHogarDeco: true,
    // Abierto a todas las marcas de Hogar y Decoración: genera contra el backend
    // (`POST /v1/generate/ambiente-completo`) y deja de ser interno —
    // ADR-2026-09-30-variantes-y-ambiente-salen-del-candado-dev (supersede, para este
    // modo, ADR-2026-09-11-los-tres-modos-de-hogar-y-deco-son-internos-hasta-que-generen).
    // "Mostrar cómo funciona" sigue `devOnly`: su Generar todavía es un mock.
  },
  {
    // Modo "Mostrar cómo funciona" de BELLEZA Y CUIDADO PERSONAL — pantalla propia
    // (ruta `/mostrar-como-funciona-belleza`); los consumidores del catálogo navegan ahí
    // por `route`, igual que Animar imagen. Video corto (5-8 s) del producto con una
    // modelo: mostrándolo a cámara, o usándolo. Sólo ese rubro (`soloBelleza`).
    // Ver ADR-2026-09-10-modo-mostrar-como-funciona-belleza.
    //
    // Es el hermano del modo homónimo de Hogar y Decoración (rama
    // `nuevos-modos-hogar-y-deco`) y NO el mismo con un rubro más: comparte la anatomía
    // de la pantalla y nada de las listas —allá el sujeto es un mueble y hay tres
    // acciones, acá siempre hay una modelo y hay dos—. Por eso son dos entradas, dos
    // `kind` y dos pantallas, como ya pasa con los dos modos de Plantillas
    // (ADR-2026-09-10-plantillas-belleza-modo-propio).
    //
    // Carátula PLACEHOLDER: la foto del carrusel de bienvenida del rubro (producto de
    // belleza presentado por una modelo). Falta un cuadro propio.
    path: "mostrar-como-funciona-belleza",
    route: "/mostrar-como-funciona-belleza",
    // Carátula propia (ADR-2026-10-12-caratula-de-mostrar-como-funciona-belleza): antes
    // compartía la foto del onboarding de Belleza.
    poster: "/images/modo_mostrar_como_funciona_belleza/caratula.jpg",
    preset: {
      id: "mostrar-como-funciona-belleza-card",
      title: "Mostrar cómo funciona",
      get description() {
        return `Un video corto de tu producto: un ${palabraDeModeloActual().singular} usándolo, o el producto solo en escena.`;
      },
      imageUrl: "/images/modo_mostrar_como_funciona_belleza/caratula.jpg",
      imageUrlFull: "/images/modo_mostrar_como_funciona_belleza/caratula.jpg",
      kind: "mostrar-como-funciona-belleza",
      outputType: "video",
      // Cobra por SEGUNDO, igual que Animar imagen: es el mismo pipeline de video
      // (ADR-2026-09-10-mostrar-como-funciona-belleza-corre-sobre-animar-imagen).
      creditosPorUnidad: 1,
      unidadConsumo: "segundo",
    },
    soloBelleza: true,
  },
  {
    // Modo "Recrear imagen" (antes "Replicar una foto",
    // ADR-2026-10-05-recrear-con-todas-subidas-y-subir-imagen) — pantalla propia (ruta `/replicar-foto`); los
    // consumidores del catálogo navegan ahí por `route`, igual que Animar imagen. El
    // usuario elige un producto y sube la foto que quiere imitar; sale esa escena con
    // su producto adentro.
    //
    // NACIÓ para Belleza y Cuidado Personal (ADR-2026-09-10-modo-replicar-foto-belleza)
    // y hoy es de TODOS los rubros — ADR-2026-09-13-replicar-una-foto-sale-del-rubro-belleza.
    // Nada del modo mira el rubro: la escena la trae el usuario y el motor es
    // `creativo_plus` con dos referencias, así que replicar la foto de una vidriera de
    // ropa o de un plato es el mismo camino que replicar una de skincare.
    //
    // Es el INVERSO del modo Plantillas y no una variante suya: allá la escena la cura
    // Atlas y el usuario pone el producto, acá la escena la trae él. Por eso no comparte
    // pantalla ni datos con `PlantillasBelleza` — una plantilla del catálogo es una fila
    // de `templates` con `base_prompt`, y acá es un archivo que subió el usuario.
    //
    // Carátula propia: la MISMA escena dos veces con el producto cambiado, más el
    // packshot que entra en el medio — ADR-2026-09-22-cada-modo-con-su-caratula.
    // La anterior (un packshot a la izquierda, una flecha azul y la escena a la
    // derecha) contaba producto → escena, que es lo que hace Automática: le faltaba
    // justo el elemento que define a este modo, la foto ajena que se quiere copiar.
    //
    // Va A SANGRE en 3:2, no con los costados transparentes como la anterior: en el
    // slot `aspect-[3/2]` de las cards el `object-cover` no recorta nada, y en el
    // hero el `object-contain` la muestra entera con su propio fondo.
    path: "replicar-foto",
    route: "/replicar-foto",
    poster: "/images/modo_replicar_foto/caratula.jpg",
    preset: {
      id: "replicar-foto-card",
      title: "Recrear imagen",
      description:
        "Subí una foto que quieras imitar y sale la misma escena con el producto de tu marca.",
      imageUrl: "/images/modo_replicar_foto/caratula.jpg",
      imageUrlFull: "/images/modo_replicar_foto/caratula.jpg",
      kind: "replicar-foto",
      // Cobra por IMAGEN entregada, como el resto de los modos de imagen: la
      // generación corre sobre `creativo_plus` y la resolución la fija el backend.
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    // Modo "Recrear video" — ADR-2026-10-09-recrear-video-adentro-de-recrear. La mitad de
    // video de Recrear (inspirada en Genjutsu de Higgsfield): un video de referencia, las
    // imágenes de lo que va en él y unas indicaciones; sale el mismo movimiento con la
    // persona, el producto o la ropa del usuario. Pantalla propia (`/recrear-video`) a la
    // que se entra desde Recrear con el interruptor en Video, y desde el chat.
    //
    // `ocultoEnListas`: no es una card más de `/modos` ni de la home; su puerta es Recrear.
    // Sigue en `MODES` por lo que no son listas (el kind, `abrir_modo`, el candado de plan).
    // Sin rubro: como Recrear imagen, nada del modo mira el rubro.
    //
    // Carátula provisoria: el cuadro de la plantilla Unboxing, hasta que tenga la suya.
    path: "recrear-video",
    route: "/recrear-video",
    ocultoEnListas: true,
    poster: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/video_tipos/unboxing.jpg",
    preset: {
      id: "recrear-video-card",
      title: "Recrear video",
      description:
        "Subí un video que te guste y Atlas lo vuelve a hacer con tu persona, tu producto o tu ropa.",
      imageUrl: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/video_tipos/unboxing.jpg",
      imageUrlFull: "https://kywexemefeblsysfxkpu.supabase.co/storage/v1/object/public/system/video_tipos/unboxing.jpg",
      kind: "recrear-video",
      outputType: "video",
      // Por segundo, como el Creador de video: la tarifa la pone la calidad (estándar hasta
      // 14 s, profesional hasta 29). Esto es el piso que muestran las listas.
      creditosPorUnidad: 2,
      unidadConsumo: "segundo",
    },
  },
  {
    // Modo "Redes" — ADR-2026-10-01-modo-redes. La herramienta de la card "Redes
    // sociales" del inicio: una pantalla propia (`/redes`, `/redes/:sesionId`) con un
    // interruptor Imagen/Video, la caja de prompt con Producto y Modelo, y el lienzo del
    // Agente para lo generado. Por detrás genera como Prompt libre (imagen) y como el
    // Creador de video (video). Sin piso de plan para entrar (como Prompt libre); generar
    // video pide Pro/Ultra adentro, como el Creador. Sólo escritorio: la ruta avisa.
    //
    // La carátula es PRESTADA de Prompt libre hasta tener la propia (pendiente en el ADR):
    // va contra ADR-2026-09-22-cada-modo-con-su-caratula a sabiendas, y es lo primero a
    // reemplazar.
    path: "redes",
    route: "/redes",
    poster: "/images/modo_prompt_libre/caratula.jpg",
    preset: {
      id: "redes-card",
      title: "Redes",
      get description() {
        return `Imágenes y videos para tus redes desde un prompt, con tu producto y tu ${palabraDeModeloActual().singular}.`;
      },
      imageUrl: "/images/modo_prompt_libre/caratula.jpg",
      imageUrlFull: "/images/modo_prompt_libre/caratula.jpg",
      kind: "redes",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    // Modo "Diseño de indumentaria" — ADR-2026-09-17-un-diseno-es-un-chat. Para
    // diseñadores de ropa: una portada con caja de prompt y planos técnicos de partida,
    // y un lienzo (el del Agente, a pantalla completa) donde cada prenda tiene Mostrar
    // en uso, Mezclar y Plano técnico. Pantalla propia con dos rutas
    // (`/diseno-de-indumentaria` y `/diseno-de-indumentaria/:disenoId`); la card enlaza
    // la portada. Sólo Moda e Indumentaria (`soloModa`); el piso de plan (Pro o Ultra) lo
    // pone `getModePlanLock('diseno-indumentaria')`, y la ruta lo vuelve a mirar
    // (ADR-2026-09-18-el-modo-diseno-se-abre-a-moda-desde-pro, que supersede el `devOnly`
    // de ADR-2026-09-18-el-modo-diseno-solo-para-dev-testers). En mobile la card se ve y
    // la ruta avisa que es de escritorio.
    path: "diseno-indumentaria",
    route: "/diseno-de-indumentaria",
    poster: "/images/modo_diseno_indumentaria/caratula.jpg",
    preset: {
      id: "diseno-indumentaria-card",
      title: "Diseño de indumentaria",
      description:
        "Partí de un plano técnico o describí la prenda: mostrala en uso, mezclá texturas e inspiraciones y sacá su plano técnico.",
      imageUrl: "/images/modo_diseno_indumentaria/caratula.jpg",
      imageUrlFull: "/images/modo_diseno_indumentaria/caratula.jpg",
      kind: "diseno-indumentaria",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
    soloModa: true,
  },
  {
    // Modo "Video con influencer" — visible para TODOS los planes desde
    // ADR-561 (con candado + bloqueo de entrada para gratis/Básico vía
    // getModePlanLock, ADR-564). Carátula en video LANDSCAPE propia (16:9,
    // para el slot 3:2 de la página Modos) + su poster. El hero MOBILE usa
    // otro asset vertical (720x960). La miniatura (portrait) es la imagen
    // "al lado de la explicación". Ver ADR-506 / ADR-509.
    path: "video-influencer",
    video: "/images/modo_video_ugc/modo_video_ugc_caratula_desktop.mp4",
    poster: "/images/modo_video_ugc/modo_video_ugc_caratula_desktop.jpg",
    // El flag de arriba vive en `modosVisibles`, que la SIDEBAR no usa: ella filtra con
    // `modoVisiblePorFlags`, que sólo mira los flags del modo. Espejarlo acá es lo que
    // hace que las cuatro superficies lo escondan con una sola regla — El `.map()` que
    // arma `HERRAMIENTAS` ya copia este flag, así que la sidebar se entera sola.
    //
    // OJO con el nombre: acá `ocultoEnListas` sólo aporta el "no aparezcas en ninguna
    // lista". Lo que en Anuncio significa además "pero seguís vivo por URL" NO aplica:
    // con el flag apagado `InterfazV2` deja su card en `null` y la URL tampoco abre.
    ocultoEnListas: !VIDEO_INFLUENCER_ENABLED,
    preset: {
      id: "video-influencer-card",
      title: "Video con influencer",
      description:
        "Generá un video de tu producto presentado por un influencer IA, con un gancho que enganche en los primeros segundos.",
      imageUrl: "/images/modo_video_ugc/modo_video_ugc_miniatura.png",
      imageUrlFull: "/images/modo_video_ugc/modo_video_ugc_miniatura.png",
      poster: "/images/modo_video_ugc/modo_video_ugc_caratula_desktop.jpg",
      kind: "video-influencer",
      outputType: "video",
      creditosPorUnidad: 1,
      unidadConsumo: "segundo",
    },
  },
  {
    path: "asistente",
    video: "/images/modo_asistente/modo_asistente_caratula_desktop.mp4",
    // Poster del clip VIVO — ADR-2026-08-20-la-caratula-del-asistente-era-de-un-clip-deprecado.
    // Acá apuntaba a `modo_asistente_caratula_mobile.jpg`, que es un frame de
    // `modo_asistente_caratula_mobile_v1.mp4` (deprecado): TRES MUJERES CLONADAS CON
    // HOLOGRAMAS. Para un modo de CHAT ("Crear desde cero") eso se lee como un modo de
    // modelos IA. Ahora es un frame del mismo clip que declara `video` acá arriba: la
    // barra de prompt escribiendo, que es lo que el modo hace.
    poster: "/images/modo_asistente/modo_asistente_poster.jpg",
    // FUERA DE LAS LISTAS — ADR-2026-09-13-crear-desde-cero-sale-de-las-listas.
    //
    // El Agente y los tres modos que lo partieron (Prompt libre, Creador de video y el
    // chat) hacen lo mismo mejor, así que ofrecerlo al lado de ellos es ofrecer dos veces
    // la misma puerta. Igual que con Anuncio
    // (ADR-2026-08-20-anuncio-sale-de-las-listas-sin-salir-del-catalogo), es un flag y no
    // un borrado de `MODES`: la entrada la siguen consumiendo `MODE_PATH_TO_KIND`, la
    // card que `InterfazV2` busca por `kind` para que `/modo/asistente` abra por URL, y
    // el `modo_utilizado: 'asistente'` con el que ya se guardaron miles de generaciones.
    ocultoEnListas: true,
    preset: {
      id: "asistente-marketing-card",
      title: "Crear desde cero",
      description: "Generá, analizá y planificá tu contenido",
      imageUrl: "/images/modo_con_modelo/fot_modelo.png",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "asistente",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    // "Asistente v2" — la MISMA pantalla que `asistente`, montada con
    // `variante='v2'` (ADR-2026-09-08-asistente-v2-es-una-variante-del-mismo-modo).
    // Sin switch Imagen/Video y sin el desplegable de Ayuda de IA: la ayuda va
    // prendida por detrás y si querés un video se lo pedís por texto.
    //
    // `devOnly`: es una variante en prueba y no tiene sentido que un usuario vea dos
    // modos casi idénticos uno al lado del otro. Sale de `/modos`, de la tira del
    // inicio, del sheet de Generar, del drawer de Mi marca y de la sidebar; el
    // deep-link `/modo/asistente-v2` sigue abriendo para cualquiera (`modosVisibles`
    // filtra listas, no rutas).
    //
    // Carátula: la MISMA que `asistente` —es el mismo modo—. No se duplican assets.
    path: "asistente-v2",
    video: "/images/modo_asistente/modo_asistente_caratula_desktop.mp4",
    poster: "/images/modo_asistente/modo_asistente_poster.jpg",
    devOnly: true,
    preset: {
      id: "asistente-v2-marketing-card",
      title: "Asistente v2",
      description: "Generá, analizá y planificá tu contenido",
      imageUrl: "/images/modo_con_modelo/fot_modelo.png",
      imageUrlFull: "/images/modo_catalogo_ia/modo_catalogo_hero.png",
      kind: "asistente-v2",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    // "Prompt libre" — ADR-2026-09-07-modo-prompt-libre. El camino directo de imagen de
    // "Crear desde cero" en una pantalla propia: prompt + referencias, sin chat ni
    // video. Es el primero de los tres modos que reemplazan al asistente, que sigue
    // listado mientras tanto.
    //
    // Salió de `devOnly` — ADR-2026-09-12-prompt-libre-y-el-chat-salen-de-interno. Estuvo
    // interno "hasta que existan los tres" para no mostrar dos entradas que hacen lo
    // mismo; el corte pasó a ser el PLAN: lo ven todos y su piso es cualquier plan pago
    // (`getModePlanLock`), así que la prueba gratis lo ve con candado.
    //
    // Carátula PROPIA — ADR-2026-09-22-cada-modo-con-su-caratula. Hasta acá era el clip
    // del asistente prestado, el MISMO que usaba Creador de video: los dos modos nacieron
    // del mismo corte y se veían idénticos en la grilla. La nueva cuenta lo que este modo
    // hace y ningún otro: la barra de escritura con la frase y las referencias adjuntas a
    // la izquierda, y la foto que salió de esa frase a la derecha.
    //
    // Sin `video`: el clip prestado se dibujaba ENCIMA del poster, así que dejarlo habría
    // tapado la carátula nueva.
    path: "prompt-libre",
    poster: "/images/modo_prompt_libre/caratula.jpg",
    preset: {
      id: "prompt-libre-card",
      title: "Prompt libre",
      get description() {
        return `Describí la imagen que querés y Atlas la genera. Sumá un producto, un ${palabraDeModeloActual().singular} o una foto como referencia.`;
      },
      imageUrl: "/images/modo_prompt_libre/caratula.jpg",
      imageUrlFull: "/images/modo_prompt_libre/caratula.jpg",
      kind: "prompt-libre",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
  {
    // "Creador de video" — ADR-2026-09-07-modo-creador-video. El asistente en modo video
    // en una pantalla propia, sin agente: prompt + referencias, y en desktop la grilla de
    // resultados a pantalla completa. Segundo de los tres modos que reemplazan al
    // asistente. `outputType: 'video'` lo pone en la familia Videos de los popups y del
    // sheet de Generar.
    //
    // También salió de `devOnly` (ADR-2026-09-12-prompt-libre-y-el-chat-salen-de-interno),
    // pero su piso NO bajó: sigue pidiendo Pro/Ultra como los otros modos que generan
    // video (Video con influencer, Animar imagen). O sea que Básico lo ve con candado.
    //
    // Carátula PROPIA y EN VIDEO — ADR-2026-09-22-cada-modo-con-su-caratula. Era el clip
    // del asistente, el MISMO que usaba Prompt libre: los dos modos nacieron del mismo
    // corte y se veían idénticos en la grilla. Es el único de los seis que se renovaron
    // que quedó en movimiento, porque es lo que este modo produce.
    //
    // El `poster` es el CUADRO 0 del propio clip (`caratula_poster.jpg`, extraído del mp4
    // ya comprimido), así lo que se ve quieto es exactamente lo que se ve al arrancar. Acá
    // sí sirve el cuadro 0: esta pieza abre con la composición entera, sin el fundido que
    // obligó a buscar el segundo 2,25 en la del asistente.
    path: "creador-video",
    video: "/images/modo_creador_video/caratula.mp4",
    poster: "/images/modo_creador_video/caratula_poster.jpg",
    preset: {
      id: "creador-video-card",
      title: "Creador de video",
      get description() {
        return `Describí el video que querés y Atlas lo genera. Sumá un producto, un ${palabraDeModeloActual().singular} o una foto como referencia.`;
      },
      imageUrl: "/images/modo_creador_video/caratula_poster.jpg",
      imageUrlFull: "/images/modo_creador_video/caratula_poster.jpg",
      kind: "creador-video",
      outputType: "video",
      creditosPorUnidad: 1,
      unidadConsumo: "segundo",
    },
  },
  {
    // Anuncio va con carátula FIJA — ADR-2026-08-04-anuncio-va-con-foto-y-sin-video.
    // Sin `video`: los consumidores caen a `<img>` con el `poster`, igual que
    // catálogo-muebles e infografía. Antes tenía dos videos (éste y el "nuevo" que le
    // pisaba el carrusel del shell), así que el modo se veía distinto según la
    // pantalla; ahora es una sola imagen en todos lados.
    //
    // La de acá es la carátula GENÉRICA, la que ve cualquier rubro. La del aviso de la
    // mesa de luz —que era ésta hasta ADR-2026-08-05-la-portada-de-anuncio-es-de-hogar—
    // vive ahora en `PORTADA_POR_RUBRO`, para Hogar y Decoración.
    path: "anuncio",
    // Fuera de las listas de descubrimiento —
    // ADR-2026-08-20-anuncio-sale-de-las-listas-sin-salir-del-catalogo. La entrada NO
    // se borra: se siguen llegando a ella el atajo de "Ediciones rápidas", los botones
    // del visor y del historial (que hacen `MODES.find(m => m.path === 'anuncio')`) y
    // el deep-link `/interfaz-v2/modo/anuncio`.
    ocultoEnListas: true,
    poster: "/images/modo_anuncio/modo_anuncio_portada.jpg",
    preset: {
      id: "anuncio-card",
      title: "Anuncio",
      description:
        "Texto, colores y formato — armá un anuncio listo para publicar.",
      // La misma foto que el `poster`: era la miniatura de modelo prestada, así que
      // la carátula del modo cambiaba según quién la pintara
      // (ADR-2026-08-04-anuncio-va-con-foto-y-sin-video).
      imageUrl: "/images/modo_anuncio/modo_anuncio_portada.jpg",
      imageUrlFull: "/images/modo_anuncio/modo_anuncio_hero.png",
      kind: "anuncio",
      creditosPorUnidad: 1,
      unidadConsumo: "imagen",
    },
  },
];

/**
 * Portadas para cajas CUADRADAS — ADR-2026-08-04-la-portada-cuadrada-no-es-de-mobile,
 * que supersede a ADR-2026-08-04-modelo-tiene-portada-propia-en-mobile.
 *
 * Un modo tiene una sola carátula (ADR-2026-08-04-el-carrusel-de-mobile-usa-las-caratulas-de-desktop),
 * y esta tabla es la excepción explícita: la carátula es apaisada y hay superficies
 * donde la caja es 1:1 o casi, así que el recorte central se come el encuadre.
 *
 * Nació como "portadas de mobile" porque las dos primeras superficies con ese problema
 * —el hero del inicio y las cards del sheet de Generar— son de teléfono. Pero lo que la
 * define no es el dispositivo sino la FORMA de la caja: el drawer "¿Qué querés generar?"
 * de Mi marca tiene cards `aspect-square` y se ve igual en desktop. Atarla a mobile
 * dejaba a esa pantalla mostrando el recorte apaisado sin ninguna razón.
 *
 * Indexada por `path` (la misma llave que `CARRUSEL_MODOS`) y no por `kind`, para que
 * quien la consulte use la conversión que ya tiene a mano. Un modo sin entrada acá
 * sigue con su carátula única, que es lo normal.
 *
 * El criterio para sumar un consumidor es la caja, no la pantalla: si dibuja la carátula
 * en 1:1 (o muy cerca), va; si la dibuja apaisada, usa la de siempre.
 */
/**
 * Carátulas que dependen del RUBRO de la marca activa —
 * ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro.
 *
 * La carátula de un modo muestra lo que el modo produce, y "lo que produce" cambia con
 * el rubro: la de Catálogo es una grilla de vistas de un traje sobre una modelo, que a
 * una marca de muebles no le dice nada de un modo que le va a fotografiar roperos.
 *
 * Indexada por rubro → `path`, las dos llaves que el llamador ya tiene a mano. Un modo
 * sin entrada para el rubro activo se queda con su carátula de siempre, que es el caso
 * normal: esta tabla es la excepción, no el mecanismo.
 *
 * Cada entrada trae la APAISADA (3:2 — la tira de la home, `/modos`) y, si el modo la
 * necesita, la CUADRADA (1:1 — hero de mobile, cards del sheet de Generar y del drawer
 * de Mi marca). Sin `cuadrada`, la apaisada sirve para las dos: es lo que pasa con
 * Catálogo, cuyo asset es vertical y con el mueble centrado, así que ningún recorte lo
 * corta. Anuncio sí tiene las dos, porque su aviso trae texto y en 1:1 hay que
 * reencuadrarlo a mano.
 */
export const PORTADA_POR_RUBRO: Record<
  string,
  Record<string, { apaisada: string; cuadrada?: string }>
> = {
  [RUBRO_HOGAR_DECO]: {
    // El ropero en contexto — el mismo asset que ya usa la card del modo "Catálogo de
    // muebles" (ADR-817), que es el catálogo de este rubro contado en una foto. El
    // rubro es "Hogar y Decoración" y no "Muebles" (ADR-2026-08-05-infografia-la-ve-el-rubro-hogar-y-decoracion):
    // son dos rubros distintos en la app y las marcas del caso están en el primero.
    catalogo: { apaisada: '/images/modo_catalogo_muebles/hero_contexto.jpg' },
    // Automática con portada propia del rubro. OJO: acá el modo pierde su carátula en
    // VIDEO (el holograma), porque una portada de rubro siempre se pinta como imagen
    // fija — ver el comentario de arriba.
    auto: {
      apaisada: '/images/modo_automatico/modo_auto_portada_hogar.jpg',
      cuadrada: '/images/modo_automatico/modo_auto_portada_hogar_cuadrada.jpg',
    },
    // El aviso de la mesa de luz — ADR-2026-08-05-la-portada-de-anuncio-es-de-hogar.
    // Era la carátula de Anuncio para TODOS los rubros: un aviso de mueble con su copy
    // ("Tu rincón de descanso perfecto") ofreciéndose como ejemplo de lo que el modo
    // hace, a marcas de moda, gastronomía o cosmética. Acá pasa a ser lo que siempre
    // fue: la portada de este rubro.
    anuncio: {
      apaisada: '/images/modo_anuncio/mueble_cama.jpg',
      cuadrada: '/images/modo_anuncio/mueble_cama_cuadrada.jpg',
    },
    // Las que armó Maxi para las cards de este rubro (ADR-2026-10-12-portadas-de-hogar): la
    // grilla del sillón (Generación en lote) y la ficha con medidas de la mesa (Infografía).
    // Sólo la card: adentro del modo siguen `hero_contexto` y `hero_foto`.
    'catalogo-muebles': { apaisada: '/images/modo_catalogo_muebles/caratula_hogar.jpg' },
    infografia: { apaisada: '/images/modo_infografia/caratula_hogar.jpg' },
  },
};

/** La carátula APAISADA propia del rubro para ese modo, o `undefined` si no tiene. */
export const portadaDeRubro = (
  path: string,
  rubro?: string | null,
): string | undefined =>
  rubro ? PORTADA_POR_RUBRO[rubro]?.[path]?.apaisada : undefined;

/**
 * La CUADRADA del rubro, con la apaisada de respaldo: un rubro que declara portada
 * propia tiene que ganarle a `PORTADA_CUADRADA` en las dos formas de caja, o la card
 * cuadrada se quedaría mostrando la portada genérica del modo.
 */
export const portadaCuadradaDeRubro = (
  path: string,
  rubro?: string | null,
): string | undefined => {
  const entrada = rubro ? PORTADA_POR_RUBRO[rubro]?.[path] : undefined;
  return entrada?.cuadrada ?? entrada?.apaisada;
};

/**
 * ── EL NOMBRE Y LA BAJADA DE CADA MODO ───────────────────────────────────────
 *
 * `COPY_MODOS` es la ÚNICA fuente de cómo se llama un modo en la interfaz
 * (ADR-2026-09-01-un-solo-nombre-y-una-sola-caratula-por-modo). Vino de `COPY_TIRA`,
 * que vivía en `EstiloMinimal` porque nació para la tira de la home; hoy la leen todas
 * las superficies que muestran modos, así que vive en el catálogo, al lado de `MODES`.
 *
 * **Por qué no alcanza con `preset.title`.** Ese título es el del CATÁLOGO —"Generar
 * imagen automáticamente", "Generar catálogo con IA"— y está pensado para leerse solo,
 * en una frase. En una card angosta, en una fila de sidebar o en un breadcrumb no
 * entra: se corta ("Generar i…") o envuelve en dos líneas y desalinea la grilla. Los de
 * acá son los cortos, los que el usuario ve en la home.
 *
 * **La bajada** es la línea de una sola frase que acompaña al nombre donde hay lugar
 * (el popup de vertiente, la grilla de `/modos`, el hero de mobile). No es la
 * `description` de `MODES`: ésa son dos renglones y medio, escritos para el detalle del
 * modo.
 *
 * **Todos los modos tienen entrada**, incluso aquellos cuyo nombre corto coincide con
 * el largo (Anuncio, Fotos de producto). Sin entrada, cada superficie caía a un
 * respaldo distinto y el mismo modo terminaba con dos nombres —era lo que pasaba con
 * "Carrusel" en la sidebar contra "Carrusel de imágenes" en la home— o directamente sin
 * bajada.
 */
export const COPY_MODOS: Record<string, { titulo: string; bajada: string }> = {
  // "Automática" y "Modelo" en vez de "Imagen automática" / "Imagen con modelo": en la
  // card angosta quedan 82 px de texto y esos títulos miden 130 y 134 px, así que
  // envolvían en dos líneas y su bloque arrancaba 17 px más arriba que el de los
  // cortos — desalineados contra las otras cuatro. Con todos en una línea, la
  // alineación sale sola.
  auto: { titulo: 'Automática', bajada: 'La IA arma la escena' },
  // El NOMBRE del modo es "Modelo" para todas las marcas y no sigue al rubro
  // (ADR-2026-09-22-en-moda-el-modelo-se-llama-modelo): ésta es la tabla de la que sale
  // el título de la card en la home, en `/modos`, en el sheet de Generar, en la sidebar
  // y en el breadcrumb, y el modo se llama igual en todas. Un string y no un `get`
  // justamente porque ya no depende de nada.
  //
  // La BAJADA sí describe a la persona, así que ésa sí sigue al rubro —"modelo" en
  // Moda, "avatar" en el resto— y queda como `get` porque esta tabla se arma una sola
  // vez al cargar el módulo y la marca activa se cambia sin recargar la app.
  modelo: {
    titulo: MODO_MODELO.titulo,
    get bajada() {
      return `Sobre un ${palabraDeModeloActual().singular} IA`;
    },
  },
  // "Generación en lote" (antes "Catálogo"): ADR-2026-10-05-la-home-ofrece-crear-o-copiar-contenido.
  catalogo: { titulo: 'Generación en lote', bajada: 'Varias vistas en una tanda' },
  // El de muebles se llama "Catálogo" y no "Muebles" —
  // ADR-2026-08-05-el-catalogo-de-muebles-se-llama-catalogo. Es EL catálogo de ese rubro:
  // `catalogo` está `ocultoEnHogarDeco` y éste es `soloHogarDeco`, así que los dos nunca
  // se ven juntos. Decir "Muebles" obligaba a la marca de muebles a traducir: en su
  // cuenta, esto ES el catálogo.
  // El Catálogo con el agente (ADR-2026-10-13-catalogo-con-el-agente): para muebles se llama
  // "Catálogo" a secas; "Generación en lote" queda para `catalogo` (Moda y el resto).
  'catalogo-agente': { titulo: 'Catálogo', bajada: 'Contale al agente qué catálogo querés' },
  infografia: { titulo: 'Infografía', bajada: 'La ficha técnica del producto' },
  // El ÚNICO nombre largo de esta tabla, y es a propósito —
  // ADR-2026-09-14-el-modo-de-video-se-llama-video-con-influencer. El resto se acorta
  // por el ancho de la card; éste se alargó porque "Video" no era el genérico de la
  // familia sino uno de cinco: en las mismas listas están Creador de video, Animar
  // imagen, Shorts Studio y el Estudio de Video. Nombre corto y `preset.title` ahora
  // coinciden: este modo dejó de tener dos nombres.
  //
  // La bajada dejó de decir "Un influencer IA lo presenta": con el influencer en el
  // nombre, esa línea repetía el título en vez de sumar. Ahora dice lo que el modo pide
  // configurar, que es lo que el usuario todavía no sabe.
  'video-influencer': { titulo: 'Video con influencer', bajada: 'Con gancho, voz y guion' },
  asistente: { titulo: 'Crear desde cero', bajada: 'Pedile contenido por chat' },
  // La variante en prueba (`devOnly`) — mismo modo, sin el switch Imagen/Video ni el
  // desplegable de Ayuda de IA. El nombre la distingue de "Crear desde cero" en el
  // breadcrumb, que es la única superficie donde hoy se lee.
  'asistente-v2': { titulo: 'Asistente v2', bajada: 'Pedile contenido por chat' },
  'prompt-libre': { titulo: 'Prompt libre', bajada: 'Describí la imagen y Atlas la genera' },
  // "Crear" y "Recrear": el par con que la home ofrece el modo Redes y Plantillas
  // (ADR-2026-10-05-crear-y-recrear; antes "Crear contenido" / "Copiar contenido"). Las
  // bajadas dicen el punto de partida, que es lo que el nombre solo no alcanza a decir.
  redes: { titulo: 'Crear', bajada: 'Desde tu idea: describí lo que querés' },
  'creador-video': { titulo: 'Creador de video', bajada: 'Describí el video y Atlas lo genera' },
  anuncio: { titulo: 'Anuncio', bajada: 'Listo para publicar' },
  // Los cuatro de abajo entraron con ADR-2026-09-01-un-solo-nombre-y-una-sola-caratula-por-modo.
  // Su título es el mismo que el del catálogo —es corto y no hay nada que acortar—, pero
  // ahora es explícito: sin entrada, cada superficie caía al respaldo por su cuenta y
  // ninguna tenía bajada.
  'fotos-producto': { titulo: 'Fotos de producto', bajada: 'Tu producto solo, en varias vistas' },
  carrusel: { titulo: 'Carrusel de imágenes', bajada: 'Nuevas tomas desde una foto' },
  'animar-imagen': { titulo: 'Animar imagen', bajada: 'Tu foto, con movimiento real' },
  'mostrar-como-funciona': { titulo: 'Mostrar cómo funciona', bajada: 'El mueble en uso, en video' },
  'variantes-de-acabado': { titulo: 'Variantes de acabado', bajada: 'El mismo mueble en cada acabado' },
  'ambiente-completo': { titulo: 'Ambiente completo', bajada: 'Varios productos en una sola foto' },
  'mostrar-como-funciona-belleza': {
    titulo: 'Mostrar cómo funciona',
    bajada: 'Tu producto en video, en uso o solo',
  },
  'replicar-foto': {
    titulo: 'Recrear imagen',
    bajada: 'La foto que te gustó, con tu producto',
  },
  'recrear-video': {
    titulo: 'Recrear video',
    bajada: 'El video que te gustó, con lo tuyo',
  },
  'diseno-indumentaria': {
    titulo: 'Diseño de indumentaria',
    bajada: 'Tu prenda, del plano al prototipo',
  },
  'shorts-studio': { titulo: 'Shorts Studio', bajada: 'Tu video, en otro estilo' },
  // Las dos herramientas que no son modos de `MODES` pero comparten las mismas listas
  // (`HERRAMIENTAS_EXTRA`): las cards de `/modos`, la sidebar y —Plantillas— los popups
  // de vertiente de la home.
  // "Plantillas" acá; "Recrear" SÓLO en la interfaz nueva, por `NOMBRES_UI_NUEVA`
  // (ADR-2026-10-07-plantillas-en-la-vieja-recrear-en-la-nueva).
  plantillas: { titulo: 'Plantillas', bajada: 'Desde una plantilla ya armada' },
  // Las plantillas de Belleza son el mismo modo para otro rubro: el mismo nombre
  // (ADR-2026-10-05-la-sidebar-por-secciones).
  'plantillas-belleza': { titulo: 'Plantillas', bajada: 'Desde una plantilla ya armada' },
  'editor-video': { titulo: 'Estudio de Video', bajada: 'Timeline, cortes y voces con IA' },
};

/**
 * Los nombres que cambian SÓLO en la interfaz nueva (grupo C,
 * ADR-2026-10-07-plantillas-en-la-vieja-recrear-en-la-nueva). "Crear / Recrear" es el par
 * de la home por destino (ADR-2026-10-05-crear-y-recrear); la home de siempre, `/modos`,
 * el teléfono y el agente siguen diciendo "Plantillas". Lo que no está acá se llama igual
 * en las dos.
 */
export const NOMBRES_UI_NUEVA: Readonly<Partial<Record<string, string>>> = {
  plantillas: 'Recrear',
  'plantillas-belleza': 'Recrear',
};

/**
 * Título y bajada de las CARDS de modo de la interfaz nueva (`GrillaDeModos` con `uiNueva`:
 * el popup de la home, el panel de la sidebar y el sheet del teléfono). Más largos que el
 * nombre corto —que sigue en la lista de la sidebar—: la card tiene lugar para decir de dónde
 * se arranca, y la bajada no lo repite, cuenta qué se hace
 * (ADR-2026-10-12-crear-desde-0-y-recrear-desde-una-plantilla).
 */
export const TARJETAS_UI_NUEVA: Readonly<Partial<Record<string, { titulo: string; bajada: string }>>> = {
  // Bajadas de hasta ~34 caracteres: en el popup van tres cards por fila y la bajada es de
  // una línea (con más, se cortaba con "…").
  auto: { titulo: 'Imagen automática', bajada: 'Atlas arma las escenas por vos' },
  redes: { titulo: 'Crear desde cero', bajada: 'Contá tu idea con tus palabras' },
  plantillas: { titulo: 'Recrear desde una plantilla', bajada: 'Elegí un diseño y sumá tu producto' },
  'plantillas-belleza': { titulo: 'Recrear desde una plantilla', bajada: 'Elegí un diseño y sumá tu producto' },
  // En los rubros sin plantillas ocupa el lugar de Recrear
  // (ADR-2026-10-12-replicar-imagen-en-lugar-de-recrear).
  'replicar-foto': { titulo: 'Recrear imagen', bajada: 'Subí una foto y sumá tu producto' },
};

/**
 * El nombre corto de un modo por su `path`, con respaldo.
 *
 * El respaldo es para lo que NO es un modo del catálogo —una plantilla, un preset
 * suelto— que llega a un componente compartido: ahí `path` no matchea y lo correcto es
 * mostrar el título que trae. Un modo real siempre tiene entrada.
 *
 * `uiNueva`: la superficie es de la interfaz nueva (la home por destino, la sidebar por
 * secciones, o una pantalla compartida cuando la cuenta está en C) y pide los nombres de
 * `NOMBRES_UI_NUEVA`. Sin la opción, el nombre de siempre.
 */
export function nombreCortoModo(
  path: string,
  respaldo = '',
  opciones?: { uiNueva?: boolean },
): string {
  if (opciones?.uiNueva) {
    const propio = NOMBRES_UI_NUEVA[path];
    if (propio) return propio;
  }
  return COPY_MODOS[path]?.titulo ?? respaldo;
}

/** La bajada de una línea de un modo, con el mismo criterio de respaldo. */
export function bajadaCortaModo(path: string, respaldo = ''): string {
  return COPY_MODOS[path]?.bajada ?? respaldo;
}

/**
 * La URL de un modo por su `path` — ADR-2026-09-08-las-cards-de-la-home-tambien-son-links.
 *
 * Es el `href` que necesitan las superficies que hoy lo abren con un `onClick`: la tira
 * de la home, el popup de vertiente, el sheet de "Generar". Con él, Cmd/Ctrl+clic abre
 * el modo en otra pestaña como en cualquier página web.
 *
 * La regla ya estaba escrita a mano en `SheetModosProducto` y en la sidebar: los modos
 * con pantalla propia (Animar, Carrusel, Shorts Studio) declaran `route` y el resto vive
 * en `/interfaz-v2/modo/<path>`. Acá queda en UN lugar, que es donde hay que tocarla si
 * un modo se muda.
 *
 * `null` para un `path` que no es un modo del catálogo: el llamador decide qué hacer
 * (hoy: dibujar un botón sin destino, que es lo correcto para algo que no tiene URL).
 */
export function rutaDeModo(path: string): string | null {
  const modo = MODES.find((m) => m.path === path);
  if (!modo) return null;
  return modo.route ?? `/interfaz-v2/modo/${modo.path}`;
}

/**
 * ── LA CARÁTULA DE CADA MODO ─────────────────────────────────────────────────
 *
 * La misma precedencia en toda la app
 * (ADR-2026-09-01-un-solo-nombre-y-una-sola-caratula-por-modo):
 *
 *  1. **La portada del rubro**, si la marca activa tiene una — y va SIN video
 *     (ADR-2026-08-05-la-caratula-de-catalogo-sigue-al-rubro): el video de Catálogo es
 *     la grilla de moda, o sea justo lo que la portada del rubro viene a reemplazar.
 *  2. **La del catálogo** (`MODES`: `poster` + `video`), que es la que se ve en la home.
 *  3. **La imagen del preset**, para los modos que no traen ni póster ni video
 *     (ADR-2026-08-05-la-tira-cae-cuando-la-entrada-no-trae-caratula).
 *
 * Esta función reemplaza a cinco copias de la misma escalera —una en la home, otra en
 * el sheet de Generar, otra en el drawer de Mi marca, otra en el hero de mobile y otra
 * en `/modos`—, que ya habían empezado a divergir: `/modos` no miraba las pisadas de
 * carátula y mostraba el hero de Catálogo donde el resto de la app mostraba la grilla.
 *
 * `respaldo` es para un preset que no es un modo: se pinta lo que traiga.
 */
export function caratulaModo(
  path: string,
  rubro?: string | null,
  respaldo?: string,
): { poster: string; video?: string } {
  const delRubro = portadaDeRubro(path, rubro);
  if (delRubro) return { poster: delRubro };
  const propia = CARATULA_PROPIA[path];
  if (propia) return propia;
  const modo = MODES.find((m) => m.path === path);
  // `||` y no `??` en el póster: el de Anuncio llegó a ser un string VACÍO a propósito
  // —su portada era el primer cuadro del video— y ahí hay que caer al respaldo.
  if (modo && (modo.poster || modo.video)) {
    return {
      poster: modo.poster || respaldo || modo.preset.imageUrl,
      video: modo.video,
    };
  }
  return { poster: respaldo || modo?.preset.imageUrl || '' };
}

/**
 * Carátulas que PISAN a las de `MODES` en la interfaz.
 *
 * Vivían en `CARRUSEL_MODOS` (`contenidoAtlas`), la tabla que armaba la tira de la home,
 * y por eso la pisada sólo la veían las pantallas que leían esa tabla: `/modos` mostraba
 * el hero de Catálogo con su video mientras la home mostraba la grilla de vistas. Al
 * mudarse acá la ve toda la app (ADR-2026-09-01-un-solo-nombre-y-una-sola-caratula-por-modo).
 *
 * Es una tabla chica y así tiene que quedarse: si una carátula es LA del modo, va en
 * `MODES`. Acá van las que difieren de la del catálogo por una razón de interfaz.
 */
const CARATULA_PROPIA: Record<string, { poster: string; video?: string }> = {
  // Sólo la grilla de vistas del mismo producto, que es justo lo que hace el modo, y
  // SIN video: el del catálogo es un hero de moda que no cuenta el modo.
  //
  // Grilla NUEVA — ADR-2026-09-23-las-cuatro-que-faltaban. La anterior ya contaba bien
  // el modo, pero era 4:3 (se recortaba en el slot 3:2), tenía fondo blanco grisáceo
  // contra el lavanda del resto de la grilla, y sus seis celdas eran todas CON modelo:
  // faltaba la mitad que el modo promete, "con modelo y sin modelo". La nueva mezcla
  // tres con modelo (frente, espalda, tres cuartos) y tres sin (tendida plana, detalle,
  // maniquí invisible).
  catalogo: { poster: '/images/modo_catalogo_ia/caratula.jpg' },
  // Plantillas no está en `MODES` —es un atajo al catálogo de escenas— pero sí en las
  // mismas listas, así que su carátula tiene que salir de acá o cada pantalla la
  // escribiría a mano. Es el collage de escenas de siempre
  // (ADR-2026-08-04-plantillas-usa-su-caratula-de-siempre); el archivo vive en
  // `modo_automatico/` por historia, no porque sea de ese modo.
  plantillas: { poster: '/images/modo_plantillas/caratula.jpg' },
  // "Crear desde cero" se presenta con SU VIDEO —el asistente escribiendo el prompt y
  // devolviendo las fotos—, no con la foto de las tres modelos que traía de `MODES`:
  // esa imagen es de otro modo y no cuenta lo que este hace
  // (ADR-2026-09-01-crear-desde-cero-se-presenta-con-su-video).
  //
  // El póster es un CUADRO DEL PROPIO VIDEO, así lo que se ve quieto es lo mismo que se
  // ve en movimiento. No es el cuadro 0: el video abre con un fundido y el primer
  // segundo es fondo azul vacío; éste es el primer cuadro con el título ya escrito
  // (2,25 s), que es el "primer frame" en el sentido de lo que el usuario ve al mirar la
  // card. Se extrajo del mp4 y vive en `public/` como
  // `modo_asistente_primer_frame.jpg`.
  asistente: {
    poster: '/images/modo_asistente/modo_asistente_primer_frame.jpg',
    video: '/images/modo_asistente/modo_asistente_caratula_desktop.mp4',
  },
};

/**
 * La carátula CUADRADA, para las cards 1:1 (el sheet de Generar, el drawer de Mi marca
 * y el hero de mobile) — ADR-2026-08-04-la-portada-cuadrada-no-es-de-mobile.
 *
 * La del rubro primero (que cae sola a su apaisada), después la tabla `PORTADA_CUADRADA`
 * y, si el modo no declara ninguna, la apaisada de `caratulaModo`: es preferible una
 * portada apaisada recortada a un hueco gris.
 */
export function caratulaCuadradaModo(
  path: string,
  rubro?: string | null,
  respaldo?: string,
): string {
  return (
    portadaCuadradaDeRubro(path, rubro) ??
    PORTADA_CUADRADA[path] ??
    caratulaModo(path, rubro, respaldo).poster
  );
}

export const PORTADA_CUADRADA: Record<string, string> = {
  // Recorte CENTRADO 1:1 de la carátula nueva — ADR-2026-09-23-las-cuatro-que-faltaban.
  // Las cuatro escenas entran enteras; lo que se pierde es la mitad del packshot chico
  // de la esquina, que es el precio de no tener un render 1:1 propio. Se acepta porque
  // la alternativa era peor: la card mostrando la botella y el hero de mobile el collage
  // de moda viejo, o sea dos modos distintos con el mismo nombre.
  auto: '/images/modo_automatico/caratula_cuadrada.jpg',
  // Recorte CENTRADO 1:1 de la carátula nueva, no un asset aparte: la composición es
  // simétrica y en 1024x1024 entran las tres piezas chicas y el resultado enteros. Si
  // se quedara con la cuadrada vieja, el modo mostraría dos mujeres distintas según la
  // superficie — ADR-2026-09-22-la-caratula-de-flujos-pasa-a-video.
  modelo: '/images/modo_con_modelo/caratula_cuadrada.jpg',
  // El nombre del archivo de Anuncio nace con el nombre correcto (`_cuadrada`); el
  // viejo de Modelo decía `_mobile` por la primera versión de esta tabla y ya no se usa.
  anuncio: '/images/modo_anuncio/modo_anuncio_portada_cuadrada.jpg',
  // El asistente entra acá con ADR-2026-08-20-la-caratula-del-asistente-era-de-un-clip-deprecado:
  // su carátula es apaisada (1080x716) y en las cajas 1:1 —el drawer de un producto, el
  // hero de mobile— el `object-cover` se comía los extremos de la barra de prompt, que
  // es justo lo único que identifica al modo. El recorte centrado a 716x716 la conserva
  // entera.
  asistente: '/images/modo_asistente/modo_asistente_poster_cuadrada.jpg',
  'asistente-v2': '/images/modo_asistente/modo_asistente_poster_cuadrada.jpg',
};

/**
 * Anuncio, tercero — ADR-2026-08-03-anuncio-tercero-tira-y-sidebar.
 *
 * En el orden natural de `MODES` cae ÚLTIMO, detrás de los dos modos de catálogo y
 * del asistente, y es de las cosas que más se usan. En vez de reordenar `MODES` —que
 * lo consumen `/modos`, la home de V2 y la fila de íconos del estilo Workspace, cada
 * una con sus propias razones de orden— se reubica en el punto de uso: la tira de la
 * home y la sidebar del shell, que son las dos listas donde el usuario lo busca.
 *
 * Genérico y con accesor: las dos listas tienen forma distinta (`HerramientaAtlas`
 * usa `id`; los modos de la tira, `path`). Si Anuncio no está —filtrado por rubro o
 * por feature flag— devuelve la lista tal cual.
 */
export function anuncioTercero<T>(
  items: T[],
  claveDe: (item: T) => string | null | undefined,
): T[] {
  const i = items.findIndex((item) => claveDe(item) === 'anuncio');
  // Tercero = índice 2. Con menos de tres ítems, `splice` lo deja al final, que es
  // lo más cerca del pedido que se puede estar.
  if (i === -1 || i === 2) return items;
  const copia = [...items];
  const [anuncio] = copia.splice(i, 1);
  copia.splice(2, 0, anuncio);
  return copia;
}

/**
 * LOS MODOS DEL HERO DE LA HOME DE MOBILE EN LA PRUEBA GRATIS —
 * ADR-2026-08-20-la-home-gratis-de-hogar-muestra-automatico-y-fotos.
 *
 * En la prueba gratis, el hero de la home de mobile (`MobileModeHero`) no lista todos
 * los modos sino una selección corta; el resto sigue accesible desde `/modos`, que no se
 * filtra. Esa selección era una sola, escrita a mano en `InterfazV2`, y estaba pensada
 * para una marca de ropa: `design-auto` + `design-modelo` + `asistente`.
 *
 * Para **Hogar y Decoración** eso no funcionaba. `design-modelo` tiene
 * `ocultoEnHogarDeco` —pone el producto sobre un modelo IA, que no aplica a un mueble—,
 * así que de los tres elegidos ese rubro veía DOS, y uno de ellos era el chat. La
 * vidriera de la prueba les mostraba lo que menos les sirve.
 *
 * Ahora son dos listas:
 *   • Hogar y Decoración → `design-auto` + `catalogo-muebles`.
 *   • el resto           → `design-auto` + `design-modelo` + `prompt-libre`.
 *
 * El tercero era `asistente` ("Crear desde cero") hasta que el modo salió de las listas
 * (ADR-2026-09-13-crear-desde-cero-sale-de-las-listas): la vidriera lo habría seguido
 * mostrando —es una lista aparte, como avisa el OJO de acá abajo— y habría sido el único
 * lugar de la app donde aparece. Lo reemplaza `prompt-libre`, que es su sucesor directo
 * para imagen y lo ven todos los rubros y planes.
 *
 * La de Hogar y Deco era `design-auto` + `fotos-producto` hasta
 * ADR-2026-08-28-fotos-de-producto-no-va-en-muebles, que ocultó `fotos-producto` para ese
 * rubro. Sin reemplazo, la vidriera de la prueba habría quedado con UNA sola card —
 * exactamente el bug que este helper existe para arreglar—. Entra `catalogo-muebles`,
 * que es el modo insignia del rubro y ya es su card destacada en "Generar → Imágenes"
 * (ADR-2026-08-20-el-modo-en-grande-de-generar-sigue-al-rubro), así que las dos
 * superficies destacan lo mismo.
 *
 * Razona por `kind` y no por `path` porque el hero consume los presets de `design-row`,
 * una lista que `InterfazV2` arma en su render y que sólo tiene `kind`.
 *
 * OJO: es un filtro APARTE del gating por plan. Abrir un modo en `getModePlanLock` no
 * alcanza para que aparezca acá —le pasó a `asistente` en
 * ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones, que quedó
 * abierto pero invisible en la home de mobile justo para el plan al que se le abrió—.
 * Si un modo tiene que estar en la vidriera de la prueba, va agregado acá.
 */
export function kindsHomeGratis(esHogarDeco: boolean): Set<string> {
  return esHogarDeco
    ? new Set(['design-auto', 'catalogo-agente'])
    : new Set(['design-auto', 'design-modelo', 'prompt-libre']);
}

/**
 * EL MODO QUE VA EN GRANDE en "Generar → Imágenes" de mobile —
 * ADR-2026-08-20-el-modo-en-grande-de-generar-sigue-al-rubro.
 *
 * Ese paso dibuja una card destacada (3:2) arriba y el resto en una grilla de a dos. La
 * destacada era el `path` fijo `'modelo'`, escrito en `SheetGenerar`, y para las marcas
 * de **Hogar y Decoración** eso no destacaba nada: `modelo` tiene `ocultoEnHogarDeco`,
 * así que el `find` no lo encontraba y los seis modos caían iguales a la grilla — la
 * pantalla se quedaba sin jerarquía justo para ese rubro.
 *
 * Ahora la elige el rubro, que es de donde ya salen las otras dos decisiones de esta
 * lista (qué modos entran y con qué carátula se ven):
 *
 *   • Hogar y Decoración → `catalogo-muebles`, que es SU "Generar catálogo con IA"
 *     (ADR-2026-08-01-hogar-deco-solo-catalogo-muebles-renombrado).
 *   • el resto           → `modelo`, como estaba.
 *
 * Devuelve un `path` y no un índice, igual que antes: si el modo se cae por rubro, por
 * plan o por feature flag, el `find` no lo encuentra y la pantalla sigue en pie sin
 * destacada, que es el comportamiento que ya había.
 */
export function modoDestacadoGenerar(esHogarDeco: boolean): string {
  return esHogarDeco ? 'catalogo-agente' : 'modelo';
}

/**
 * Carrusel al final — ADR-2026-08-20-el-modo-en-grande-de-generar-sigue-al-rubro.
 *
 * El gemelo de `anuncioTercero`, y por el mismo motivo: se reubica en el PUNTO DE USO en
 * vez de reordenar `MODES`, que lo consumen `/modos`, la tira del inicio y la sidebar,
 * cada una con sus propias razones de orden.
 *
 * En el orden natural del catálogo, Carrusel cae ANTES que todo lo demás en la familia
 * de imágenes, así que para una marca de Hogar y Decoración era la primera card de la
 * grilla siendo el modo menos importante del rubro.
 *
 * Genérico y con accesor por la misma razón que `anuncioTercero`: las listas que lo
 * usan tienen forma distinta. Si Carrusel no está —filtrado por sujeto, por rubro o por
 * feature flag— devuelve la lista tal cual.
 */
export function carruselUltimo<T>(
  items: T[],
  claveDe: (item: T) => string | null | undefined,
): T[] {
  const i = items.findIndex((item) => claveDe(item) === 'carrusel');
  if (i === -1 || i === items.length - 1) return items;
  const copia = [...items];
  const [carrusel] = copia.splice(i, 1);
  copia.push(carrusel);
  return copia;
}

/**
 * Path de modo → `kind` del preset. El inverso del `MODE_KIND_TO_PATH` que vive en
 * `InterfazV2`, derivado de `MODES` en vez de escrito a mano para que no puedan
 * discrepar.
 *
 * Lo necesita quien tiene el PATH y quiere preguntar por el plan: `getModePlanLock`
 * razona sobre `kind`, y la sidebar del shell lista rutas (ADR-2026-08-03-sidebar-candado-modos-sin-plan).
 */
export const MODE_PATH_TO_KIND: Record<string, string> = {
  ...Object.fromEntries(
    MODES.map((m) => [m.path, m.preset.kind]).filter(
      (par): par is [string, string] => typeof par[1] === 'string',
    ),
  ),
};

/**
 * `kind` del preset → path del modo. El inverso del de arriba, y también derivado de
 * `MODES` — ADR-2026-08-04-nombres-de-modo-iguales-en-mobile.
 *
 * Estaba escrito a mano en `InterfazV2` (idéntico, hasta que alguien agregara un modo
 * en un lado y no en el otro). Lo necesita todo el que tiene un preset y quiere el
 * NOMBRE del modo: los nombres viven en `COPY_TIRA`, indexados por path.
 */
export const MODE_KIND_TO_PATH: Record<string, string> = {
  ...Object.fromEntries(
    MODES.map((m) => [m.preset.kind, m.path]).filter(
      (par): par is [string, string] => typeof par[0] === 'string',
    ),
  ),
};

/**
 * EL CATÁLOGO DEL RUBRO — ADR-2026-08-20-el-slug-catalogo-no-tiene-card-en-hogar-y-deco.
 *
 * "Generar catálogo con IA" son DOS `kind` distintos según el rubro de la marca activa:
 * `catalogo-muebles` para Hogar y Decoración y `catalogo-ia` para todos los demás. Cada
 * uno está OCULTO para el rubro del otro (`soloHogarDeco` / `ocultoEnHogarDeco`), y en
 * `InterfazV2` eso se traduce en que su card sintética sale `null`.
 *
 * El problema que resuelve: hay entradas que llegan al modo con el slug ESCRITO A MANO
 * —el tab "Catálogo" de la barra inferior de mobile (`NavInferiorMobile`), el botón
 * "Nuevo catálogo" del estilo Workspace, y cualquier URL compartida—. Ese slug fijo es
 * `catalogo`, o sea `catalogo-ia`, o sea justo el kind que Hogar y Decoración NO tiene:
 * el efecto de ruta se encuentra la card en `null`, sale por `if (!card) return` —una
 * rama que existe para ESPERAR a que el rubro resuelva, ADR-2026-08-05-el-deep-link-de-un-modo-espera-a-su-rubro—
 * y la espera no termina nunca. El tab queda activo y no monta nada.
 *
 * Se resuelve acá y no en cada entrada a propósito: los llamadores hardcodean el slug
 * porque NO tienen por qué saber de rubros —la barra inferior sólo sabe si la marca es
 * de Moda—, y parchear uno por uno deja afuera al siguiente que se escriba y a los
 * deep-links, que no tienen a quién parchear.
 *
 * Es SIMÉTRICO: el caso espejo —una marca que no es de Hogar y Deco entrando a
 * `/interfaz-v2/modo/catalogo-muebles` por un link compartido o por cambiar de marca con
 * la URL abierta— se cuelga por el mismo motivo. Las dos direcciones son "mostrame el
 * catálogo" y la respuesta correcta es el catálogo que esa marca tiene.
 *
 * `infografia` NO entra en el swap, y es deliberado: no es "el catálogo del otro rubro"
 * sino un modo que fuera de Hogar y Decoración no existe. Mandarlo a `catalogo-ia`
 * abriría un modo que el usuario no pidió; que ahí no pase nada es correcto.
 */
export function kindDeModoParaRubro<T extends string | null | undefined>(
  kind: T,
  esHogarDeco: boolean,
): T {
  // Desde ADR-2026-10-14-catalogo-reemplaza-a-catalogo-muebles el catálogo de Hogar y
  // Decoración es `catalogo-agente`; `catalogo-muebles` sólo llega por un link viejo y va al
  // nuevo (o a `catalogo-ia` si la marca no es del rubro).
  if ((kind === 'catalogo-ia' || kind === 'catalogo-muebles') && esHogarDeco) return 'catalogo-agente' as T;
  if ((kind === 'catalogo-muebles' || kind === 'catalogo-agente') && !esHogarDeco) return 'catalogo-ia' as T;
  return kind;
}

/**
 * Modos en los que se puede ELEGIR un modelo —
 * ADR-2026-08-04-desde-un-modelo-se-elige-el-modo.
 *
 * Lo pregunta el sheet de modos cuando se abre desde un modelo de "Marca → Modelos":
 * ofrecer ahí un modo que no tiene picker de modelo sería llevar al usuario a una
 * pantalla donde lo que eligió no aparece por ningún lado.
 *
 * Se razona por `kind` y no por `path` porque es el `kind` el que decide el flow:
 *
 *   • `design-modelo`    → `DesignModeloPanel` con el picker de Modelo.
 *   • `catalogo-ia`      → `CatalogoCardsFullscreen`, que tiene botón de Modelo
 *                          cuando la tanda incluye poses con modelo (las de default).
 *   • `video-influencer` → la sección "Modelo" del panel de influencer, donde además
 *                          es OBLIGATORIO elegirlo.
 *
 * Quedan afuera, y a propósito: `design-auto` (el panel corre con
 * `hideAutoPickers`), `anuncio` (`AnuncioPanel`: textos, colores y formato),
 * `catalogo-muebles` e `infografia` (sus vistas son solo-producto) y `asistente`
 * (el modelo entra como adjunto del chat, que no se puede precargar por el canal de
 * restore).
 */
const KINDS_CON_MODELO = new Set([
  'design-modelo',
  'catalogo-ia',
  'video-influencer',
]);

export function modoAceptaModelo(kind: string | null | undefined): boolean {
  return !!kind && KINDS_CON_MODELO.has(kind);
}

/**
 * Modos en los que se puede partir de un PRODUCTO del catálogo —
 * ADR-2026-08-20-desde-un-producto-tambien-hay-modos-que-no-aplican.
 *
 * El gemelo de `KINDS_CON_MODELO`, para el otro sujeto del sheet. Existía el eje
 * "abierto desde un modelo" y NO existía el de producto, así que desde "Marca →
 * Productos" se ofrecían modos que ni siquiera miran el producto elegido: se tocaba una
 * remera y se caía en "elegí una imagen generada", donde esa remera no está.
 *
 * **Esta lista no es una opinión: es la de `InterfazV2.tsx`.** Son exactamente los
 * `kind` que su initializer de paso manda a `choose-product` / `select-vistas` /
 * `configure` cuando le llega `initialNavProducts`. Si mañana un modo empieza a aceptar
 * producto allá, tiene que entrar acá — y al revés.
 *
 * Quedan afuera, y por qué:
 *   • `carrusel` y `animar-imagen` → arrancan en `GeneradasPickGrid`, que lista
 *     `imagenes_generadas`. Ni siquiera leen `location.state`: un producto del catálogo
 *     no es una fila de esa tabla.
 *   • `shorts-studio` → parte de un video propio que el usuario sube.
 *   • `asistente` → `InterfazV2` corta con `if (k === 'asistente') return 'asistente'`
 *     ANTES de mirar `initialNavProducts`, y `AsistenteMarketingSheet` nunca recibe los
 *     productos: el chat los toma como adjunto, que este canal no precarga. Es el mismo
 *     motivo por el que `KINDS_CON_MODELO` lo deja afuera.
 */
const KINDS_CON_PRODUCTO = new Set([
  'design-auto',
  'design-modelo',
  'fotos-producto',
  'catalogo-ia',
  'catalogo-muebles',
  'catalogo-agente',
  'infografia',
  'anuncio',
  'video-influencer',
]);

export function modoAceptaProducto(kind: string | null | undefined): boolean {
  return !!kind && KINDS_CON_PRODUCTO.has(kind);
}

/**
 * Contexto con el que se decide qué modos ve alguien.
 *
 * Todos los campos son opcionales y el default es el CASO ABIERTO (nada filtrado por
 * ese eje), salvo `isDevTester`, cuyo default es `false` — un modo interno no se
 * muestra "por las dudas".
 */
export interface ContextoModosVisibles {
  /** `company_profiles.dev_tester`. Sólo `true` abre los modos `devOnly`. */
  isDevTester?: boolean | null;
  /** `true` si la marca activa es del rubro "Hogar y Decoración". */
  esHogarDeco?: boolean;
  /** `true` si la marca activa es del rubro "Belleza y Cuidado Personal". El gemelo
   *  del de arriba, para el flag `soloBelleza` — el default `false` esconde esos modos
   *  en las superficies que todavía no resolvieron el rubro, que es lo correcto:
   *  mostrarlos y rebotar al tocarlos es peor que no ofrecerlos. */
  esBelleza?: boolean;
  /** `true` si la marca activa es de "Moda e Indumentaria". El gemelo de `esBelleza`. */
  esModa?: boolean;
  /**
   * `true` si la lista se está abriendo DESDE un modelo. Recorta a los modos donde el
   * modelo se puede elegir (ADR-2026-08-04-desde-un-modelo-se-elige-el-modo). Lo usan
   * el sheet de Generar y el drawer de Mi marca; `/modos` y la sidebar no.
   */
  esModelo?: boolean;
  /**
   * `true` si la lista se está abriendo DESDE un producto del catálogo. El gemelo del
   * de arriba — ADR-2026-08-20-desde-un-producto-tambien-hay-modos-que-no-aplican.
   *
   * Ojo con el default `false`: las superficies SIN sujeto (`/modos`, la tira del
   * inicio, la sidebar, el sheet de Generar de la barra inferior) tienen que seguir
   * ofreciendo Animar y Carrusel, porque ahí no hay ningún producto que contradecir.
   */
  esProducto?: boolean;
}

/**
 * QUIÉN VE QUÉ MODOS. Fuente de verdad única —
 * ADR-2026-08-20-el-filtro-de-modos-estaba-copiado-en-cuatro-lugares.
 *
 * Esta misma lista de condiciones estaba escrita CUATRO veces —`Modos.tsx`,
 * `useModosGenerar.ts`, `SheetModosProducto.tsx` y el filtro de la sidebar en
 * `EstiloMinimal.tsx`— y ya habían divergido: las tres primeras chequeaban `devOnly` y
 * la sidebar no, así que un modo interno salía de `/modos` y del sheet pero seguía
 * listado en "Todas las herramientas". Agregar un eje nuevo significaba acordarse de
 * cuatro archivos, y el cuarto era justo el que nadie tocaba.
 *
 * Pura y exportada para poder testearla. El orden de `MODES` se respeta.
 *
 * OJO con la sidebar: no consume `MODES` sino `HERRAMIENTAS` de `contenidoAtlas.ts`,
 * que se arma con un `.map()` sobre `MODES`. Ese map tiene que COPIAR los flags o acá
 * llegan todos en `undefined` y la función no filtra nada.
 */
/** Los cuatro flags de visibilidad que un modo declara en el catálogo. */
export interface FlagsVisibilidadModo {
  devOnly?: boolean;
  ocultoEnListas?: boolean;
  soloHogarDeco?: boolean;
  ocultoEnHogarDeco?: boolean;
  soloBelleza?: boolean;
  soloModa?: boolean;
}

/**
 * El predicado, sobre los FLAGS solos.
 *
 * Existe aparte de `modosVisibles` porque hay un consumidor que no lista `ModeCard`
 * sino `HerramientaAtlas` (la sidebar, vía `contenidoAtlas`), y forzarlo a mapear de
 * ida y vuelta para poder filtrar era peor que compartir el predicado. Ese consumidor
 * venía chequeando los mismos ejes A MANO y por `id` —`if (h.id === 'infografia')
 * return esRubroHogarDeco(...)`—, o sea una quinta copia escrita de otra forma.
 */
export function modoVisiblePorFlags(
  flags: FlagsVisibilidadModo,
  ctx: {
    isDevTester?: boolean | null;
    esHogarDeco?: boolean;
    esBelleza?: boolean;
    esModa?: boolean;
  } = {},
): boolean {
  const { isDevTester = false, esHogarDeco = false, esBelleza = false, esModa = false } = ctx;
  // Fuera de las listas de descubrimiento, pero vivo en el catálogo: sigue alcanzable
  // por atajo, por acción sobre una imagen y por URL.
  if (flags.ocultoEnListas) return false;
  // Modos de muebles (catálogo + infografías): sólo marcas de Hogar y Decoración
  // (ADR-2026-07-31-modos-muebles-por-rubro-de-marca).
  if (flags.soloHogarDeco && !esHogarDeco) return false;
  // Inverso: los modos que ponen el producto sobre un modelo IA no aplican a un mueble
  // (ADR-2026-07-31-hogar-deco-sin-plantillas-ni-modelo).
  if (flags.ocultoEnHogarDeco && esHogarDeco) return false;
  // Modos de Belleza y Cuidado Personal: sólo marcas de ese rubro
  // (ADR-2026-09-10-modo-mostrar-como-funciona-belleza).
  if (flags.soloBelleza && !esBelleza) return false;
  // Modo Diseño de indumentaria: sólo marcas de Moda e Indumentaria
  // (ADR-2026-09-17-el-modo-diseno-es-solo-para-moda).
  if (flags.soloModa && !esModa) return false;
  // Modos internos: sólo dev testers.
  if (flags.devOnly && isDevTester !== true) return false;
  return true;
}

export function modosVisibles(
  modos: ModeCard[],
  ctx: ContextoModosVisibles = {},
): ModeCard[] {
  const { esModelo = false, esProducto = false } = ctx;
  return modos.filter((m) => {
    // "Video con influencer" oculto para todos mientras el flag esté off. Es un flag
    // global del módulo y no del modo, por eso no entra en `modoVisiblePorFlags`.
    if (m.path === 'video-influencer' && !VIDEO_INFLUENCER_ENABLED) return false;
    if (!modoVisiblePorFlags(m, ctx)) return false;
    // Abierto desde un modelo: los otros modos no se muestran apagados sino que no se
    // muestran — no es una restricción de plan que se levante pagando, es que ahí el
    // modelo no entra a ningún lado.
    if (esModelo && !modoAceptaModelo(m.preset.kind)) return false;
    // Ídem desde un producto: los modos que arrancan de una imagen ya generada no lo
    // miran, así que ofrecerlos es mandar al usuario a una pantalla donde lo que
    // acaba de elegir no existe.
    if (esProducto && !modoAceptaProducto(m.preset.kind)) return false;
    return true;
  });
}
